// src/components/ListasTrabajo/GestionListas/CompartirListaDialog.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box, Chip, IconButton,
    CircularProgress, Autocomplete, Popper,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';

import {
    listasTrabajoGetCompartidosJWT,
    listasTrabajoSetCompartidosJWT,
    listasTrabajoPersonalCompartirJWT,
} from '../../../api/endpoints';

const CustomPopper = (props) => (
    <Popper {...props} style={{ ...props.style, zIndex: 1600 }} placement="bottom-start" />
);

const CompartirListaDialog = ({ open, onClose, lista, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [nameMap, setNameMap] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const debounceRef = useRef(null);

    // Cargar compartidos actuales al abrir
    useEffect(() => {
        if (!open || !lista) return;
        setLoading(true);
        setSelectedIds([]);
        setNameMap({});
        setSearchQuery('');
        listasTrabajoGetCompartidosJWT(lista.Id).then(resp => {
            if (resp?.ok && resp.personas) {
                const ids = resp.personas.map(p => p.id);
                const names = {};
                resp.personas.forEach(p => { names[p.id] = p.nombre; });
                setSelectedIds(ids);
                setNameMap(names);
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [open, lista]);

    // Búsqueda con debounce
    useEffect(() => {
        if (searchQuery.length < 2) {
            setSearchOptions([]);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const resp = await listasTrabajoPersonalCompartirJWT(searchQuery);
                if (resp?.ok) {
                    setSearchOptions((resp.personas || []).filter(p => !selectedIds.includes(p.id)));
                }
            } catch { /* ignore */ }
            setSearchLoading(false);
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchQuery, selectedIds]);

    const handleSave = useCallback(async () => {
        if (!lista) return;
        setSaving(true);
        try {
            const resp = await listasTrabajoSetCompartidosJWT(lista.Id, selectedIds);
            if (resp?.ok) {
                onSuccess?.();
                onClose();
            }
        } catch { /* ignore */ }
        setSaving(false);
    }, [lista, selectedIds, onClose, onSuccess]);

    return (
        <Dialog
            open={open}
            onClose={() => !saving && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <ShareIcon color="primary" />
                        <Typography variant="h6">Compartir lista</Typography>
                    </Box>
                    <IconButton onClick={() => !saving && onClose()} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {lista?.Nombre || ''}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary">
                            Buscá por nombre o apellido (min. 2 caracteres)
                        </Typography>
                        <Autocomplete
                            multiple
                            options={searchOptions}
                            value={[]}
                            renderTags={() => null}
                            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                            getOptionLabel={(o) => o?.nombre || String(o?.id || '')}
                            inputValue={searchQuery}
                            onInputChange={(e, v, reason) => {
                                if (reason !== 'reset') setSearchQuery(v || '');
                            }}
                            onChange={(e, newValue) => {
                                const last = newValue?.length ? newValue[newValue.length - 1] : null;
                                if (!last) return;
                                const pid = Number(last.id);
                                if (!Number.isFinite(pid)) return;
                                setSelectedIds(prev => prev.includes(pid) ? prev : [...prev, pid]);
                                setNameMap(prev => ({ ...prev, [pid]: last.nombre || String(pid) }));
                                setSearchQuery('');
                            }}
                            disableCloseOnSelect={false}
                            selectOnFocus
                            clearOnBlur
                            filterSelectedOptions
                            onClose={() => setSearchQuery('')}
                            PopperComponent={CustomPopper}
                            filterOptions={(x) => x}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.id}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PersonIcon fontSize="small" />
                                        <Typography variant="body2">{opt.nombre || `#${opt.id}`}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Buscar por apellido o nombre"
                                    sx={{ mt: 1 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {selectedIds.length === 0 ? (
                            <Alert severity="info" sx={{ mt: 1.5 }}>
                                Aún no está compartida con nadie.
                            </Alert>
                        ) : (
                            <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                                {selectedIds.map(id => (
                                    <Chip
                                        key={id}
                                        label={nameMap[id] || String(id)}
                                        onDelete={() => setSelectedIds(prev => prev.filter(x => x !== id))}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => onClose()} disabled={saving} color="inherit">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={saving}
                    sx={{ borderRadius: '4px' }}
                >
                    {saving ? 'Guardando...' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CompartirListaDialog;
